import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Upload,
  message,
  Tag,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Modal,
  Input,
  Progress,
  Alert,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  LogoutOutlined,
  FileOutlined,
  KeyOutlined,
  CloudUploadOutlined,
  LoadingOutlined,
  SwapOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { fileApi, authApi, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE_TEXT, formatFileSize, type UploadProgress, type DownloadProgress } from '../services/api';
import { formatBeijingDateTime } from '../utils/date';
import './Dashboard.css';

const { Header, Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;

interface FileRecord {
  id: number;
  original_filename: string;
  file_size: number;
  status: string;
  created_at: string;
  reviewed_at?: string;
  review_comment?: string;
  upload_comment?: string;
  is_forwarded?: boolean;
  forwarded_from?: string;
}

interface ForwardUser {
  id: number;
  username: string;
  email: string;
  auth_type: string;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadComment, setUploadComment] = useState('');
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // 上传进度状态（上传字节完成后仍可能处于服务器处理阶段，如病毒扫描）
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  /** uploading：传输中；scanning：请求体已发出，等待服务端扫描与响应 */
  const [uploadPhase, setUploadPhase] = useState<'uploading' | 'scanning' | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  
  // 下载进度状态
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const downloadAbortRef = useRef<AbortController | null>(null);

  // 中转
  const [forwardModalVisible, setForwardModalVisible] = useState(false);
  const [forwardFile, setForwardFile] = useState<FileRecord | null>(null);
  const [forwardUsers, setForwardUsers] = useState<ForwardUser[]>([]);
  const [selectedTargetUserIds, setSelectedTargetUserIds] = useState<number[]>([]);
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardUsersLoading, setForwardUsersLoading] = useState(false);
  const [forwardUserSearchText, setForwardUserSearchText] = useState('');

  const filteredForwardUsers = useMemo(() => {
    let result = forwardUsers.filter((u) => u.id !== user?.id);
    if (forwardUserSearchText) {
      const searchLower = forwardUserSearchText.toLowerCase();
      result = result.filter(
        (u) =>
          u.username.toLowerCase().includes(searchLower) ||
          (u.email || '').toLowerCase().includes(searchLower)
      );
    }
    return result;
  }, [forwardUsers, forwardUserSearchText, user?.id]);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const data = await fileApi.list();
      setFiles(data);
    } catch (error: any) {
      message.error(error.error || '加载文件列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!pendingUploadFile) return;
    
    // 检查文件大小
    if (pendingUploadFile.size > MAX_UPLOAD_SIZE) {
      message.error(`文件大小超过限制，最大允许 ${MAX_UPLOAD_SIZE_TEXT}`);
      return;
    }
    
    setUploading(true);
    setUploadPhase('uploading');
    setUploadProgress({
      loaded: 0,
      total: pendingUploadFile.size,
      percent: 0,
      speed: 0,
      speedText: '0 B/s',
      remaining: 0,
      remainingText: '--',
    });
    
    // 创建取消控制器
    uploadAbortRef.current = new AbortController();
    
    try {
      const uploadRes = await fileApi.uploadWithProgress(
        pendingUploadFile,
        uploadComment,
        (progress) => {
          setUploadProgress(progress);
        },
        uploadAbortRef.current,
        () => setUploadPhase('scanning')
      );
      message.success(uploadRes?.message || '文件上传成功');
      setUploadModalOpen(false);
      setPendingUploadFile(null);
      setUploadComment('');
      setUploadProgress(null);
      loadFiles();
    } catch (error: any) {
      if (error.code === 'ERR_CANCELED') {
        message.info('上传已取消');
      } else {
        message.error(error.error || '文件上传失败');
      }
    } finally {
      setUploading(false);
      setUploadPhase(null);
      setUploadProgress(null);
      uploadAbortRef.current = null;
    }
  };
  
  // 取消上传
  const handleCancelUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
  };

  const handleDownload = async (fileId: number, filename: string) => {
    setDownloadingFileId(fileId);
    setDownloadProgress(null);
    downloadAbortRef.current = new AbortController();
    
    try {
      const blob = await fileApi.downloadWithProgress(
        fileId,
        (progress) => {
          setDownloadProgress(progress);
        },
        downloadAbortRef.current
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('文件下载成功');
    } catch (error: any) {
      if (error.code === 'ERR_CANCELED') {
        message.info('下载已取消');
      } else {
        message.error(error.error || '文件下载失败');
      }
    } finally {
      setDownloadingFileId(null);
      setDownloadProgress(null);
      downloadAbortRef.current = null;
    }
  };
  
  // 取消下载
  const handleCancelDownload = () => {
    if (downloadAbortRef.current) {
      downloadAbortRef.current.abort();
    }
  };

  const handleForward = async (record: FileRecord) => {
    setForwardFile(record);
    setSelectedTargetUserIds([]);
    setForwardUserSearchText('');
    setForwardModalVisible(true);
    setForwardUsersLoading(true);
    try {
      const users = await fileApi.listUsersForForward();
      setForwardUsers(users || []);
    } catch (error: any) {
      message.error(error.error || '加载用户列表失败');
      setForwardUsers([]);
    } finally {
      setForwardUsersLoading(false);
    }
  };

  const confirmForward = async () => {
    if (!forwardFile) return;
    if (selectedTargetUserIds.length === 0) {
      message.error('请选择目标用户');
      return;
    }

    setForwardLoading(true);
    try {
      await fileApi.forward([forwardFile.id], selectedTargetUserIds);
      message.success('文件中转成功');
      setForwardModalVisible(false);
      setForwardFile(null);
      setSelectedTargetUserIds([]);
      setForwardUserSearchText('');
    } catch (error: any) {
      message.error(error.error || '文件中转失败');
    } finally {
      setForwardLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (downloadAbortRef.current) {
        downloadAbortRef.current.abort();
      }
    };
  }, []);

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      message.error('请填写所有字段');
      return;
    }

    if (newPassword.length < 6) {
      message.error('新密码至少6位');
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error('两次输入的新密码不一致');
      return;
    }

    try {
      await authApi.changePassword(oldPassword, newPassword);
      message.success('密码修改成功');
      setChangePasswordVisible(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      message.error(error.error || '密码修改失败');
    }
  };

  const formatFileSizeLocal = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; text: string }> = {
      pending: { color: 'orange', text: '待审核' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const columns = [
    {
      title: '文件名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 320,
      ellipsis: true,
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 110,
      render: (size: number) => formatFileSizeLocal(size),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '上传时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatBeijingDateTime(date),
    },
    {
      title: '备注',
      dataIndex: 'upload_comment',
      key: 'upload_comment',
      width: 260,
      ellipsis: true,
      render: (v: string | undefined) => (v ? <span>{v}</span> : <span style={{ color: '#999' }}>-</span>),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      render: (_: any, record: FileRecord) => (
        <Space size={0}>
          {record.status === 'approved' && (
            downloadingFileId === record.id ? (
              <div style={{ minWidth: 150 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <LoadingOutlined />
                  <span style={{ fontSize: 12, color: '#666' }}>
                    {downloadProgress?.speedText || '准备中...'}
                  </span>
                  <Button type="link" size="small" danger onClick={handleCancelDownload}>
                    取消
                  </Button>
                </div>
                <Progress 
                  percent={downloadProgress?.percent || 0} 
                  size="small" 
                  status="active"
                  format={(p) => `${p}%`}
                />
              </div>
            ) : (
              <>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(record.id, record.original_filename)}
                >
                  下载
                </Button>
                <Button
                  type="link"
                  icon={<SwapOutlined />}
                  onClick={() => handleForward(record)}
                >
                  中转
                </Button>
              </>
            )
          )}
        </Space>
      ),
    },
  ];

  const approvedCount = files.filter((f) => f.status === 'approved').length;
  const pendingCount = files.filter((f) => f.status === 'pending').length;

  return (
    <Layout className="dashboard-layout">
      <Header className="dashboard-header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              color: '#ffffff', 
              fontSize: '20px', 
              fontWeight: '700',
              letterSpacing: '1px'
            }}>
              Raina
            </span>
            <Title level={4} style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: '400' }}>
              FTP审核系统
            </Title>
          </div>
          <Space>
            <span style={{ color: '#fff' }}>欢迎, {user?.username}</span>
            {user?.is_admin && (
              <Button
                type="primary"
                onClick={() => navigate('/admin')}
              >
                管理后台
              </Button>
            )}
            {user?.auth_type === 'local' && (
              <Button
                icon={<KeyOutlined />}
                onClick={() => setChangePasswordVisible(true)}
              >
                修改密码
              </Button>
            )}
            <Button
              icon={<LogoutOutlined />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >
              退出
            </Button>
          </Space>
        </div>
      </Header>
      <Content className="dashboard-content">
        <div className="content-container">
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="总文件数"
                  value={files.length}
                  prefix={<FileOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="待审核"
                  value={pendingCount}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="已通过"
                  value={approvedCount}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
          </Row>

          <Card
            title="文件管理"
            extra={
              <Upload
                beforeUpload={(file) => {
                  setPendingUploadFile(file);
                  setUploadComment('');
                  setUploadModalOpen(true);
                  return false; // 阻止自动上传
                }}
                showUploadList={false}
                maxCount={1}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={uploading}
                >
                  上传文件
                </Button>
              </Upload>
            }
          >
            <Table
              className="dashboard-files-table"
              columns={columns}
              dataSource={files}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </div>

        <Modal
          title="提交审核"
          open={uploadModalOpen}
          onCancel={() => {
            if (!uploading) {
              setUploadModalOpen(false);
              setPendingUploadFile(null);
              setUploadComment('');
              setUploadProgress(null);
              setUploadPhase(null);
            }
          }}
          footer={uploading ? [
            <Button key="cancel" danger onClick={handleCancelUpload}>
              {uploadPhase === 'scanning' ? '取消（中断扫描）' : '取消上传'}
            </Button>
          ] : [
            <Button key="back" onClick={() => {
              setUploadModalOpen(false);
              setPendingUploadFile(null);
              setUploadComment('');
            }}>
              取消
            </Button>,
            <Button key="submit" type="primary" onClick={handleUpload}>
              提交
            </Button>
          ]}
          closable={!uploading}
          maskClosable={!uploading}
        >
          {pendingUploadFile && (
            <>
              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: 0 }}>
                  <strong>文件:</strong> {pendingUploadFile.name}
                </p>
                <p style={{ margin: '4px 0', color: '#666' }}>
                  <strong>大小:</strong> {formatFileSize(pendingUploadFile.size)}
                </p>
                {pendingUploadFile.size > MAX_UPLOAD_SIZE && (
                  <Alert 
                    type="error" 
                    message={`文件大小超过限制，最大允许 ${MAX_UPLOAD_SIZE_TEXT}`}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
              
              {uploading && uploadPhase === 'uploading' && uploadProgress && (
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 16, 
                  borderRadius: 8,
                  marginBottom: 16
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span><CloudUploadOutlined /> 正在上传...</span>
                    <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                      {uploadProgress.speedText}
                    </span>
                  </div>
                  <Progress 
                    percent={uploadProgress.percent} 
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                  />
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    marginTop: 8,
                    fontSize: 12,
                    color: '#666'
                  }}>
                    <span>
                      {formatFileSize(uploadProgress.loaded)} / {formatFileSize(uploadProgress.total)}
                    </span>
                    <span>
                      剩余时间: {uploadProgress.remainingText}
                    </span>
                  </div>
                </div>
              )}
              
              {uploading && uploadPhase === 'scanning' && (
                <div style={{
                  background: '#f6ffed',
                  border: '1px solid #b7eb8f',
                  padding: 24,
                  borderRadius: 8,
                  marginBottom: 16,
                  textAlign: 'center',
                }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16, fontWeight: 600, color: '#389e0d' }}>
                    正在安全扫描…
                  </div>
                  <p style={{ margin: '12px 0 0', fontSize: 12, color: '#666' }}>
                    文件已上传完毕，服务器正在处理，请稍候。
                  </p>
                </div>
              )}
              
              <div style={{ marginTop: 12 }}>
                <TextArea
                  rows={4}
                  placeholder="备注（可选）"
                  value={uploadComment}
                  onChange={(e) => setUploadComment(e.target.value)}
                  disabled={uploading}
                />
              </div>
              
              <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
                <span>最大上传文件大小: {MAX_UPLOAD_SIZE_TEXT}</span>
              </div>
            </>
          )}
        </Modal>

        <Modal
          title="修改密码"
          open={changePasswordVisible}
          onCancel={() => {
            setChangePasswordVisible(false);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
          }}
          onOk={handleChangePassword}
          okText="确认修改"
          cancelText="取消"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <p><strong>旧密码:</strong></p>
              <Input.Password
                placeholder="请输入旧密码"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
            </div>
            <div>
              <p><strong>新密码:</strong></p>
              <Input.Password
                placeholder="请输入新密码（至少6位）"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <p><strong>确认新密码:</strong></p>
              <Input.Password
                placeholder="请再次输入新密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onPressEnter={handleChangePassword}
              />
            </div>
          </div>
        </Modal>

        <Modal
          title={`文件中转 - ${forwardFile?.original_filename || ''}`}
          open={forwardModalVisible}
          onCancel={() => {
            setForwardModalVisible(false);
            setForwardFile(null);
            setSelectedTargetUserIds([]);
            setForwardUserSearchText('');
          }}
          onOk={confirmForward}
          okText="确认中转"
          cancelText="取消"
          confirmLoading={forwardLoading}
          width={700}
        >
          <div style={{ marginBottom: 16 }}>
            <strong>中转文件:</strong>{' '}
            <span>{forwardFile?.original_filename}</span>
          </div>
          <div>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>选择目标用户:</strong>
              <Input
                placeholder="搜索用户名或邮箱"
                prefix={<SearchOutlined />}
                value={forwardUserSearchText}
                onChange={(e) => setForwardUserSearchText(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
            </div>
            <Table
              columns={[
                { title: '用户名', dataIndex: 'username', key: 'username' },
                { title: '邮箱', dataIndex: 'email', key: 'email' },
                {
                  title: '认证方式',
                  dataIndex: 'auth_type',
                  key: 'auth_type',
                  render: (v: string) => (
                    <Tag color={v === 'local' ? 'blue' : 'orange'}>
                      {v === 'local' ? '本地' : 'IPA'}
                    </Tag>
                  ),
                },
              ]}
              dataSource={filteredForwardUsers}
              rowKey="id"
              loading={forwardUsersLoading}
              rowSelection={{
                selectedRowKeys: selectedTargetUserIds,
                onChange: (keys) => setSelectedTargetUserIds(keys as number[]),
              }}
              pagination={{ pageSize: 5 }}
              size="small"
            />
          </div>
        </Modal>
      </Content>
    </Layout>
  );
};

export default Dashboard;
